import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState
} from "react";
import { apiClient, responseData } from "@/api/client";
import type { User } from "@/api/types";

interface AuthValue {
  user: User | null;
  loading: boolean;
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    responseData(apiClient.GET("/api/v1/identity/auth/me"), true)
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (username: string, password: string) => {
    const result = await responseData(apiClient.POST("/api/v1/identity/auth/login", {
      body: { username, password },
    }), true);
    setUser(result.user);
  };

  const logout = async () => {
    await responseData(apiClient.POST("/api/v1/identity/auth/logout"), true);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider no está configurado.");
  return value;
}
